// Cognito Hosted UI による認証。
// implicit flow (response_type=token) を使い、redirect 後のフラグメントから
// id_token を取り出して localStorage に保存。401 が返ったら token を捨てて
// 再ログインに飛ばす。
//
// AUTH_ENABLED が false (= esbuild --define で COGNITO_* が埋め込まれていない)
// 場合はノーオペで素通り (ローカル開発用)。

import {
  AUTH_ENABLED,
  COGNITO_CLIENT_ID,
  COGNITO_DOMAIN,
  COGNITO_IDP_NAME,
  COGNITO_REDIRECT_URI,
} from './config.js';

const TOKEN_KEY = 'feelsogood:id_token';
const EXP_KEY = 'feelsogood:id_token_exp';
/** トークン期限切れ判定の余裕 (秒)。30s 前にはリログさせる */
const REFRESH_MARGIN_SEC = 30;

interface ParsedFragment {
  readonly idToken: string;
  readonly expSec: number;
}

function parseHashFragment(): ParsedFragment | null {
  const hash = window.location.hash;
  if (!hash.startsWith('#')) return null;
  const params = new URLSearchParams(hash.slice(1));
  const idToken = params.get('id_token');
  const expiresIn = params.get('expires_in');
  if (idToken === null || expiresIn === null) return null;
  const expIn = Number(expiresIn);
  if (!Number.isFinite(expIn)) return null;
  return { idToken, expSec: Math.floor(Date.now() / 1000) + expIn };
}

function loginRedirect(): never {
  const url = new URL(`${COGNITO_DOMAIN}/login`);
  url.searchParams.set('response_type', 'token');
  url.searchParams.set('client_id', COGNITO_CLIENT_ID);
  url.searchParams.set('redirect_uri', COGNITO_REDIRECT_URI);
  url.searchParams.set('scope', 'openid email');
  if (COGNITO_IDP_NAME !== '') {
    url.searchParams.set('identity_provider', COGNITO_IDP_NAME);
  }
  window.location.replace(url.toString());
  // never returns; 型のためにダミー
  throw new Error('redirecting to login');
}

function getStoredToken(): string | null {
  const t = localStorage.getItem(TOKEN_KEY);
  const exp = Number(localStorage.getItem(EXP_KEY) ?? '0');
  if (t === null) return null;
  if (exp <= Math.floor(Date.now() / 1000) + REFRESH_MARGIN_SEC) return null;
  return t;
}

function storeToken(p: ParsedFragment): void {
  localStorage.setItem(TOKEN_KEY, p.idToken);
  localStorage.setItem(EXP_KEY, String(p.expSec));
}

function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXP_KEY);
}

/**
 * アプリ初期化前に呼ぶ。AUTH_ENABLED の場合のみ動く。
 * - URL fragment に id_token が乗ってるなら拾って保存し、URL を綺麗にする
 * - 期限内の token があれば素通り
 * - 無ければ Hosted UI にリダイレクト (この関数からは戻らない)
 */
export function ensureAuthenticated(): void {
  if (!AUTH_ENABLED) return;
  const fragment = parseHashFragment();
  if (fragment) {
    storeToken(fragment);
    history.replaceState({}, '', window.location.pathname + window.location.search);
    return;
  }
  if (getStoredToken() === null) {
    loginRedirect();
  }
}

/**
 * fetch のラッパ。AUTH_ENABLED なら Authorization: Bearer を付与し、
 * 401 が返ったら token を破棄して再ログイン。それ以外はそのまま fetch。
 */
export async function authFetch(input: string, init?: RequestInit): Promise<Response> {
  if (!AUTH_ENABLED) {
    return fetch(input, init);
  }
  const token = getStoredToken();
  if (token === null) loginRedirect();
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    loginRedirect();
  }
  return res;
}
